import { useEffect, useRef } from "react";
import { identityFromStored, initSodium, type IdentityKeys } from "../lib/crypto";
import { getStoredJwt } from "../lib/authApi";
import { loadIdentity, setActiveUserId, type IdentityRow } from "../lib/db";
import type { BootKey } from "../app/state/domainTypes";

type BootState = "pending" | "active" | "done" | "error";

type UseBootSequenceArgs = {
  setBoot: (key: BootKey, state: BootState, detail?: string) => void | Promise<void>;
  onBootError: (message: string) => void;
  setSodiumReady: (ready: boolean) => void;
  setIdentityRow: (row: IdentityRow | null) => void;
  setKeys: (keys: IdentityKeys | null) => void;
  setLoginEmail: (email: string) => void;
  hideBoot: () => void;
};

export function useBootSequence({
  setBoot,
  onBootError,
  setSodiumReady,
  setIdentityRow,
  setKeys,
  setLoginEmail,
  hideBoot,
}: UseBootSequenceArgs) {
  const bootTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (bootTimeoutRef.current) window.clearTimeout(bootTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const scheduleBootHide = (delay: number) => {
      if (bootTimeoutRef.current) window.clearTimeout(bootTimeoutRef.current);
      bootTimeoutRef.current = window.setTimeout(() => hideBoot(), delay);
    };

    void (async () => {
      try {
        setBoot("sodium", "active", "loading runtime");
        await initSodium();
        await setBoot("sodium", "done", "libsodium ready");

        setBoot("vault", "active", "checking IndexedDB");
        const row = await loadIdentity();
        setActiveUserId(row?.userId ?? null);
        setIdentityRow(row ?? null);
        if (row && getStoredJwt()) {
          const restored = await identityFromStored(row.userId, row.username, row.publicKeyEdB64, row.secretKeyEdB64);
          setKeys(restored);
          await setBoot("vault", "done", `identity @${row.username}`);
        } else {
          if (row) setLoginEmail(row.email);
          await setBoot("vault", "done", row ? "session expired — sign in again" : "no local identity");
        }

        await setBoot("format", "done", "magic bytes STXR");
        setSodiumReady(true);

        if (!row || !getStoredJwt()) {
          scheduleBootHide(950);
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        onBootError(message);
        setBoot("sodium", "error", message);
      }
    })();
  }, [hideBoot, onBootError, setBoot, setIdentityRow, setKeys, setLoginEmail, setSodiumReady]);
}
